import { Injectable } from '@angular/core';
import { Storage, ref, uploadBytes, getDownloadURL, listAll, deleteObject } from '@angular/fire/storage';
import { Observable, from, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FirebaseStorageService {
  constructor(private storage: Storage) {}

  /**
   * Carica un file nel bucket di Firebase Storage
   * @param file Il file da caricare
   * @param path Il percorso nel bucket dove salvare il file
   * @returns Observable con l'URL del file caricato
   */
  uploadFile(file: File, path: string): Observable<string> {
    const storageRef = ref(this.storage, path);
    return from(uploadBytes(storageRef, file).then(() => getDownloadURL(storageRef)));
  }

  /**
   * Ottiene l'URL di download di un file
   * @param path Il percorso del file nel bucket
   * @returns Observable con l'URL del file
   */
  getFileUrl(path: string): Observable<string> {
    const storageRef = ref(this.storage, path);
    return from(getDownloadURL(storageRef));
  }

  /**
   * Lista tutti i file in una directory
   * @param path Il percorso della directory nel bucket
   * @returns Observable con la lista degli URL dei file
   */
  listFiles(path: string): Observable<string[]> {
    const storageRef = ref(this.storage, path);
    return from(listAll(storageRef).then(result => {
      return Promise.all(result.items.map(item => getDownloadURL(item)));
    }));
  }

  /**
   * Lista tutti i file in una directory (versione Promise)
   * @param path Il percorso della directory nel bucket
   * @returns Promise con la lista degli URL dei file
   */
  async listFilesPromise(path: string): Promise<string[]> {
    return firstValueFrom(this.listFiles(path));
  }

  /**
   * Elimina un file dal bucket
   * @param path Il percorso del file da eliminare
   * @returns Observable che si completa quando il file è stato eliminato
   */
  deleteFile(path: string): Observable<void> {
    const storageRef = ref(this.storage, path);
    return from(deleteObject(storageRef));
  }

  /**
   * Scarica un file dal bucket di Firebase Storage
   * @param path Il percorso del file nel bucket
   * @returns Observable con il blob del file
   */
  downloadFile(path: string): Observable<Blob> {
    const storageRef = ref(this.storage, path);
    return from(getDownloadURL(storageRef).then(url => {
      return fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Errore durante il download del file: ${response.status} ${response.statusText}`);
          }
          return response.blob();
        })
        .catch(error => {
          throw new Error(`Errore durante il download del file: ${error.message}`);
        });
    }));
  }
}
